/**
 * Synchronously parses the persisted `auth` slice out of
 * `localStorage['persist:root']`. Bridges the redux-persist double
 * encoding (root JSON → per-slice JSON string → fields) so callers can
 * read the slice before Redux has finished rehydrating.
 *
 * Dependency-free so callers in the persist / pre-rehydration paths
 * don't pull in the rest of `@/models/authModel`.
 *
 * ---
 * **HELD BACK FROM `@heidi/auth` ON PURPOSE — do not "fix" this omission.**
 *
 * This file is otherwise package-eligible: it is pure and import-free, and by
 * every other measure it belongs behind a package door. It stays here for one
 * reason only — it is named in `persistence-manifest.json` under the
 * `persist:root` key, and a *pure path rename* is enough to regenerate that
 * manifest. The `monorepo-auth-package` change proves it moves no
 * persisted-state owner by shipping a **zero-diff** manifest on every slice
 * (spec `auth-package-persistence`); a non-zero diff means a slice is wrong,
 * not that a migration is owed.
 *
 * The `AuthProvider` enum that used to sit beside this reader in
 * `./persistedAuthProvider` DID move (to `@heidi/auth/contracts`) — it has no
 * dependency on this half. `getPersistedAuthProvider()` stays with this file.
 *
 * Whether this reader eventually moves or is deleted outright is owned by the
 * Rematch handoff, when the owning teams retire `persist:root`'s `auth` entry.
 */
export function readPersistedAuthSlice<T>(): T | undefined {
  if (globalThis.window === undefined) return undefined;

  try {
    const persistedRoot = localStorage.getItem('persist:root');
    if (!persistedRoot) return undefined;

    const parsed = JSON.parse(persistedRoot) as { auth?: string };
    if (!parsed.auth) return undefined;

    return JSON.parse(parsed.auth) as T;
  } catch {
    return undefined;
  }
}
